import { AuthService } from './authService';
import { extractApiError } from '../utils/apiError';
import {
  SystemRuleMaintenanceDto,
  CreateSystemRuleRequest,
  UpdateSystemRuleRequest,
  SqlValidationResult,
  SqlPreviewResult,
  RuleFunctionTypeDto,
} from '../types/systemRuleMaintenance';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class SystemRuleMaintenanceService {
  private baseUrl = `${API_BASE_URL}/system-rules`;

  /**
   * Get all system rules for current tenant
   */
  async getAll(): Promise<SystemRuleMaintenanceDto[]> {
    const response = await AuthService.authenticatedGet(this.baseUrl);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }

  /**
   * Get only user-defined rules (RuleType=1)
   */
  async getUserRules(): Promise<SystemRuleMaintenanceDto[]> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/user-rules`);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }

  /**
   * Get a specific rule by RuleID
   */
  async getByRuleId(ruleId: number): Promise<SystemRuleMaintenanceDto | null> {
    try {
      const response = await AuthService.authenticatedGet(`${this.baseUrl}/${ruleId}`);
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        const error = await response.json().catch(() => null);
        throw new Error(extractApiError(error, response, 'HTTP error!'));
      }
      return await response.json();
    } catch (error: any) {
      if (error.message?.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Create a new user rule
   */
  async create(request: CreateSystemRuleRequest): Promise<SystemRuleMaintenanceDto> {
    const response = await AuthService.authenticatedPost(this.baseUrl, request);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }

  /**
   * Update an existing user rule
   */
  async update(ruleId: number, request: UpdateSystemRuleRequest): Promise<SystemRuleMaintenanceDto> {
    const response = await AuthService.authenticatedPut(`${this.baseUrl}/${ruleId}`, request);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }

  /**
   * Delete a user rule
   */
  async delete(ruleId: number): Promise<void> {
    const response = await AuthService.authenticatedDelete(`${this.baseUrl}/${ruleId}`);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
  }

  /**
   * Validate SQL expression syntax
   */
  async validateSql(sqlExpression: string): Promise<SqlValidationResult> {
    const response = await AuthService.authenticatedPost(`${this.baseUrl}/validate-sql`, {
      sqlExpression,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }

  /**
   * Preview SQL expression (dry run)
   */
  async previewSql(sqlExpression: string, maxRows: number = 10): Promise<SqlPreviewResult> {
    const response = await AuthService.authenticatedPost(`${this.baseUrl}/preview-sql`, {
      sqlExpression,
      maxRows,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }

  /**
   * Get available rule function types
   */
  async getFunctionTypes(): Promise<RuleFunctionTypeDto[]> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/function-types`);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }

  /**
   * Search rules by name or SQL expression
   */
  async search(term: string): Promise<SystemRuleMaintenanceDto[]> {
    const response = await AuthService.authenticatedGet(
      `${this.baseUrl}/search?term=${encodeURIComponent(term)}`
    );
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }
}

export const systemRuleMaintenanceService = new SystemRuleMaintenanceService();
export default systemRuleMaintenanceService;
